/**
 * Which of our lines a line on a supplier's document is, decided by Jev among
 * candidates code already found.
 *
 * Two screens read a supplier's paperwork. "Read A Supplier Quote" puts its
 * lines onto an order and has to find each one's catalogue product; the
 * receipt check compares a receipt with the order and has to find each line's
 * order line. Both match by word overlap (`matchQuotedLine`,
 * `describesSameProduct`), and word overlap cannot tell `TPO Cleaner 1Gal./Pail`
 * from `TPO Mem. Cleaner (20 squares)`: they share the words that matter and
 * differ in the ones that decide the price. So the rep hunts the dropdown.
 *
 * Here code shortlists, Jev chooses. Each supplier line is one TypeSafe Choice
 * whose options are only that line's shortlist plus "none", so the model can
 * never pick a product code did not offer. The thresholds that decide what a
 * choice is allowed to do live here, not in the function that calls Jev
 * (ADR 0155).
 *
 * `supplierMatchRequest` and `readSupplierMatch` are transcribed into
 * `supabase/functions/quote-match/request.ts`, and
 * `supabase/test/quote-match-request.test.ts` holds the copies together.
 */

import { QUOTE_MATCH_MODEL, QUOTE_MATCH_NONE } from "./quoteMatch.js";
import {
  normalizeTokens,
  similarity,
  SHOW_ALTERNATIVE_AT,
  type CatalogueProduct,
  type MatchedLine,
} from "./quoteImport.js";
import { wordOverlap } from "./reconcile.js";

/** At or above this, Jev's choice is applied: the line is linked. */
export const SUPPLIER_MATCH_CONFIDENT_AT = 0.9;
/** At or above this, Jev's choice is offered first for a person to confirm. */
export const SUPPLIER_MATCH_POSSIBLE_AT = 0.6;
/** Options per line. Enough to hold every plausible pail and pack size. */
export const SUPPLIER_MATCH_SHORTLIST = 8;

/**
 * What the `quote-match` function accepts in one supplier-lines request. It
 * refuses the whole request past any of these, so `supplierMatchBatches`
 * never sends past them. Transcribed into the function as `SUPPLIER_LIMITS`;
 * the drift test holds the two equal.
 */
export const SUPPLIER_MATCH_LIMITS = {
  lines: 100,
  descriptionMax: 300,
  unitMax: 30,
  optionsPerLine: 20,
} as const;

/** A line on the supplier's document, keyed so the answer comes back to it. */
export interface DocumentLine {
  key: string;
  description: string;
  unit?: string | null;
}

/** One thing a supplier line could be: a catalogue product or an order line. */
export interface MatchOption {
  id: string;
  name: string | null;
  unit?: string | null;
}

/** What Jev chose for one line. */
export interface SupplierMatch {
  key: string;
  /** The option chosen, or null for none of them. */
  id: string | null;
  probability: number;
}

/**
 * The catalogue products worth asking about for one quoted line, best first:
 * everything word overlap rates at least worth showing a person.
 */
export function catalogueShortlist(
  description: string,
  catalogue: readonly CatalogueProduct[],
  limit: number = SUPPLIER_MATCH_SHORTLIST,
): CatalogueProduct[] {
  const wanted = normalizeTokens(description);
  return catalogue
    .filter((p) => p.active !== false)
    .map((product) => ({
      product,
      score: similarity(wanted, normalizeTokens(product.name)),
    }))
    .filter((s) => s.score >= SHOW_ALTERNATIVE_AT)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.product);
}

const optionKey = (index: number): string => `option_${index + 1}`;

const optionLabel = (option: MatchOption): string =>
  [option.name ?? "", option.unit ? `(sold by ${option.unit})` : null]
    .filter(Boolean)
    .join(" ");

/**
 * The TypeSafe request: one Choice per supplier line over its own shortlist.
 *
 * The supplier's wording is state, never instructions. Quantities and prices
 * are left out: "same product" is a question about what the thing is, and a
 * price that happens to agree is how a wrong pack size gets matched.
 */
export function supplierMatchRequest(
  lines: readonly DocumentLine[],
  options: Readonly<Record<string, readonly MatchOption[]>>,
): { model: string; state: unknown; questions: Record<string, unknown> } {
  const asked = lines.filter((line) => (options[line.key] ?? []).length > 0);
  const state = {
    context:
      "Lines from a roofing supplier's quote or receipt, and for each line the roofing company's own products it might be. " +
      "Suppliers abbreviate, reorder words and write sizes their own way.",
    supplier_lines: asked.map((line) => ({
      description: line.description,
      unit: line.unit ?? "",
    })),
  };

  const questions: Record<string, unknown> = {};
  asked.forEach((line, index) => {
    const criteria: Record<string, string> = {};
    (options[line.key] ?? []).forEach((option, n) => {
      criteria[optionKey(n)] = optionLabel(option);
    });
    criteria[QUOTE_MATCH_NONE] =
      "None of these. The supplier's line is a different product, or a different size, pack, gauge, colour or coverage of one.";
    questions[line.key] = {
      type: "choice",
      instructions:
        `Which of our products is the supplier's line \`supplier_lines[${index}]\`? ` +
        "It must be the same product in the same size and pack: a 1-gallon pail and a 20-square pail of the same cleaner are different products, and so are 4x8 and 4x10 boards. " +
        "Abbreviations and word order do not make a different product.",
      criteria,
    };
  });

  return { model: QUOTE_MATCH_MODEL, state, questions };
}

/** Read Jev's answers back to the option each line chose. */
export function readSupplierMatch(
  answers: unknown,
  lines: readonly DocumentLine[],
  options: Readonly<Record<string, readonly MatchOption[]>>,
): SupplierMatch[] {
  const all = (answers ?? {}) as Record<string, unknown>;
  return lines
    .filter((line) => (options[line.key] ?? []).length > 0)
    .map((line) => {
      const answer = all[line.key] as
        { choice?: unknown; probabilities?: unknown } | undefined;
      const choice = typeof answer?.choice === "string" ? answer.choice : null;
      const probabilities = (answer?.probabilities ?? {}) as Record<
        string,
        unknown
      >;
      const byKey = new Map(
        (options[line.key] ?? []).map((option, n) => [optionKey(n), option]),
      );
      const option = choice ? byKey.get(choice) : undefined;
      const known = option !== undefined || choice === QUOTE_MATCH_NONE;
      const probability = Number(choice ? probabilities[choice] : 0);
      return {
        key: line.key,
        id: option ? option.id : null,
        probability:
          known && Number.isFinite(probability)
            ? Math.min(1, Math.max(0, probability))
            : 0,
      };
    });
}

/** One request's worth of lines, as the function accepts them. */
export interface SupplierMatchBatch {
  lines: { key: string; description: string; unit: string | null }[];
  options: Record<string, string[]>;
}

/**
 * The requests to send for these lines, each inside `SUPPLIER_MATCH_LIMITS`.
 *
 * One line the function would refuse makes it refuse the whole request, and
 * the screens then fall back to word overlap for every line. So nothing is
 * sent that could be refused: a line with no options or no words is left out
 * (there is nothing to ask), a long description or unit is cut to the cap, a
 * line keeps only its first `optionsPerLine` options (callers pass them best
 * first), a repeated key is sent once, and the lines go in batches of at most
 * `lines`. Every line keeps its own key, so answers from all the batches merge
 * back by key.
 */
export function supplierMatchBatches(
  lines: readonly DocumentLine[],
  options: Readonly<Record<string, readonly string[]>>,
): SupplierMatchBatch[] {
  const L = SUPPLIER_MATCH_LIMITS;
  const seen = new Set<string>();
  const sendable: SupplierMatchBatch["lines"] = [];
  const kept: Record<string, string[]> = {};
  for (const line of lines) {
    if (seen.has(line.key)) continue;
    const description = line.description.trim().slice(0, L.descriptionMax);
    const ids = [...new Set(options[line.key] ?? [])].slice(
      0,
      L.optionsPerLine,
    );
    if (description === "" || ids.length === 0) continue;
    seen.add(line.key);
    const unit = line.unit?.trim().slice(0, L.unitMax);
    sendable.push({ key: line.key, description, unit: unit ? unit : null });
    kept[line.key] = ids;
  }

  const batches: SupplierMatchBatch[] = [];
  for (let at = 0; at < sendable.length; at += L.lines) {
    const batchLines = sendable.slice(at, at + L.lines);
    batches.push({
      lines: batchLines,
      options: Object.fromEntries(batchLines.map((l) => [l.key, kept[l.key]!])),
    });
  }
  return batches;
}

/**
 * The order lines worth offering one receipt line, best first, at most
 * `limit` of them.
 *
 * Every unplaced order line is a candidate (word overlap already failed to
 * place these, so a line sharing no word may still be the one), but a receipt
 * against a long order cannot offer all of them. Ranked by words shared, then
 * by the share of the shorter description, then by the order's own order.
 */
export function orderLineShortlist(
  description: string,
  orderLines: readonly { id: string; name: string | null }[],
  limit: number = SUPPLIER_MATCH_LIMITS.optionsPerLine,
): string[] {
  return orderLines
    .map((line, index) => ({
      id: line.id,
      index,
      ...wordOverlap(line.name ?? "", description),
    }))
    .sort(
      (a, b) => b.shared - a.shared || b.share - a.share || a.index - b.index,
    )
    .slice(0, limit)
    .map((s) => s.id);
}

const percent = (p: number) => `${Math.round(p * 100)}%`;

/** A unit as written, for comparing: trimmed and uppercased, or null. */
const unitOf = (unit: string | null | undefined): string | null => {
  const u = (unit ?? "").trim().toUpperCase();
  return u === "" ? null : u;
};

/**
 * A quote's word-overlap matches, with Jev's choices applied.
 *
 *   - Jev sure (at or above `SUPPLIER_MATCH_CONFIDENT_AT`) of a product: the
 *     line is linked to it, whatever word overlap thought.
 *   - Jev fairly sure (at or above `SUPPLIER_MATCH_POSSIBLE_AT`) of the very
 *     product word overlap had already linked: it stays linked. Agreement is
 *     not a reason to make a person look.
 *   - Jev fairly sure of any other product: that product is offered first,
 *     for a person to say yes to.
 *   - Either way, a product sold by a different unit than the supplier's line
 *     (BD against SQ, once trimmed and uppercased) is only ever offered, never
 *     linked: the same words in a different unit is a different price. A
 *     blank unit on either side has nothing to disagree with.
 *   - Jev sure it is none of them: a word-overlap link is taken back to a
 *     suggestion, because two tools disagreeing is exactly when a person
 *     should look.
 *   - Anything less: the line is as word overlap left it.
 *
 * `matched[i]` is answered by the match whose key is `String(i)`.
 */
export function withSupplierMatches(
  matched: readonly MatchedLine[],
  answers: readonly SupplierMatch[],
  catalogue: readonly CatalogueProduct[],
): MatchedLine[] {
  const byKey = new Map(answers.map((a) => [a.key, a]));
  const byId = new Map(catalogue.map((p) => [p.id, p]));

  return matched.map((line, i) => {
    const answer = byKey.get(String(i));
    if (!answer) return line;
    const chosen = answer.id ? byId.get(answer.id) : undefined;
    const others = (current: CatalogueProduct | null) =>
      [current, ...line.alternatives].filter(
        (p): p is CatalogueProduct => Boolean(p) && p!.id !== chosen?.id,
      );

    if (chosen && answer.probability >= SUPPLIER_MATCH_POSSIBLE_AT) {
      const sure = answer.probability >= SUPPLIER_MATCH_CONFIDENT_AT;
      const agreesWithWords =
        line.confidence === "confident" && line.product?.id === chosen.id;
      const theirs = unitOf(line.unit);
      const ours = unitOf(chosen.unit);
      const unitsDiffer = theirs !== null && ours !== null && theirs !== ours;
      const p = percent(answer.probability);

      if (unitsDiffer) {
        return {
          ...line,
          product: chosen,
          confidence: "possible",
          why: `Jev thinks this is "${chosen.name}" (${p} sure), but the quote sells it by ${theirs} and the catalogue by ${ours}. Confirm it.`,
          alternatives: others(line.product),
        };
      }
      if (sure || agreesWithWords) {
        return {
          ...line,
          product: chosen,
          confidence: "confident",
          why: sure
            ? `Jev matched "${chosen.name}" (${p} sure).`
            : `Matched "${chosen.name}", and Jev agrees (${p} sure).`,
          alternatives: others(line.product),
        };
      }
      return {
        ...line,
        product: chosen,
        confidence: "possible",
        why: `Jev thinks this is "${chosen.name}" (${p} sure). Confirm it.`,
        alternatives: others(line.product),
      };
    }
    if (
      !chosen &&
      answer.probability >= SUPPLIER_MATCH_CONFIDENT_AT &&
      line.confidence === "confident"
    ) {
      return {
        ...line,
        confidence: "possible",
        why: `The words match "${line.product?.name}", but Jev is ${percent(answer.probability)} sure it is a different product. Check it.`,
      };
    }
    return line;
  });
}

/**
 * For the receipt check: which order line each supplier line is, where Jev is
 * sure. Keyed by the line's own `key`, never its description: two receipt
 * lines can read exactly alike, and an answer about one is not an answer about
 * the other.
 */
export function receiptLineMatches(
  answers: readonly SupplierMatch[],
  lines: readonly DocumentLine[],
): Map<string, string> {
  const asked = new Set(lines.map((l) => l.key));
  const out = new Map<string, string>();
  const taken = new Set<string>();
  for (const answer of [...answers].sort(
    (a, b) => b.probability - a.probability,
  )) {
    if (!answer.id || answer.probability < SUPPLIER_MATCH_CONFIDENT_AT)
      continue;
    if (taken.has(answer.id)) continue;
    if (!asked.has(answer.key) || out.has(answer.key)) continue;
    taken.add(answer.id);
    out.set(answer.key, answer.id);
  }
  return out;
}
